import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtUSD, num } from "@/lib/format";
import { toast } from "sonner";
import { Save, Crown, TrendingUp } from "lucide-react";

export default function IncomeGoal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.plan === "pro" && user?.subscription?.status === "active";
  const [goal, setGoal] = useState("2000");
  const [perShift, setPerShift] = useState("");
  const [savedShifts, setSavedShifts] = useState([]);

  useEffect(() => {
    api.get("/shift-calculations").then((r) => setSavedShifts(r.data)).catch(() => {});
  }, []);

  const r = useMemo(() => {
    const g = num(goal), p = num(perShift);
    if (!p) return { shifts: 0, perWeek: 0, annual: 0 };
    const shifts = Math.ceil(g / p);
    return { shifts, perWeek: shifts / 4.33, annual: shifts * p * 12 };
  }, [goal, perShift]);

  const save = async () => {
    if (!isPro) { toast.error("Upgrade to RN Level Up Pro to save income plans."); navigate("/upgrade"); return; }
    try {
      await api.post("/income-goals", {
        name: `${fmtUSD(num(goal))}/mo goal`,
        inputs: { goal, perShift }, results: r,
      });
      toast.success("Income plan saved!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Income Goal Planner</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]">How much extra income do you want to make?</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm lg:col-span-3">
          <div>
            <Label htmlFor="goal" className="text-sm">Extra monthly income goal ($)</Label>
            <Input id="goal" type="number" inputMode="decimal" data-testid="income-goal-input" value={goal}
              onChange={(e) => setGoal(e.target.value)} className="mt-1.5 rounded-xl" placeholder="e.g. 2000" />
          </div>
          <div>
            <Label htmlFor="perShift" className="text-sm">Average pay per extra shift ($)</Label>
            <Input id="perShift" type="number" inputMode="decimal" data-testid="income-pershift-input" value={perShift}
              onChange={(e) => setPerShift(e.target.value)} className="mt-1.5 rounded-xl" placeholder="e.g. 800" />
          </div>
          {savedShifts.length > 0 && (
            <div className="rounded-xl bg-[#EAF4FA] p-3">
              <p className="text-xs font-medium text-slate-500">Pull from a saved shift calculation:</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {savedShifts.slice(0, 5).map((sc) => (
                  <button key={sc.id} data-testid={`pull-shift-${sc.id}`} onClick={() => setPerShift(String(Math.round(sc.results?.total || 0)))}
                    className="rounded-full bg-white px-3 py-1 text-xs font-medium text-[#18A6A6] shadow-sm">
                    {fmtUSD(sc.results?.total || 0)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl bg-gradient-to-br from-[#0F2747] to-[#16355F] p-6 text-white shadow-lg" data-testid="income-result-card">
            <TrendingUp className="h-6 w-6 text-[#18A6A6]" />
            <p className="mt-3 text-sm text-slate-200">
              You would need approximately
            </p>
            <p className="font-heading text-5xl font-extrabold" data-testid="income-shifts">{r.shifts}</p>
            <p className="text-sm text-slate-200">extra shift{r.shifts === 1 ? "" : "s"} this month to reach your {fmtUSD(num(goal))} goal.</p>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
              <div className="flex justify-between text-slate-200"><span>Extra shifts / month</span><span className="font-semibold text-white">{r.shifts}</span></div>
              <div className="flex justify-between text-slate-200"><span>Extra shifts / week</span><span className="font-semibold text-white">≈ {r.perWeek.toFixed(1)}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-2 font-heading font-bold"><span>Est. added annual gross</span><span className="text-[#D6A84B]">{fmtUSD(r.annual)}</span></div>
            </div>
          </div>
          <Button onClick={save} data-testid="save-income-btn"
            className={`w-full rounded-full py-6 ${isPro ? "bg-[#18A6A6] hover:bg-[#148f8f]" : "bg-[#0F2747] hover:bg-[#16355F]"}`}>
            {isPro ? <><Save className="mr-2 h-4 w-4" /> Save Plan</> : <><Crown className="mr-2 h-4 w-4 text-[#D6A84B]" /> Save Plan (Pro)</>}
          </Button>
          <p className="text-center text-xs text-slate-400">This is a gross income estimate before taxes and deductions.</p>
        </div>
      </div>
    </div>
  );
}
